import React from 'react';
import Select from 'react-select';
import { FilterOptionOption } from 'react-select/dist/declarations/src/filters';
import { BookLocation } from '../../../../../dorian-shared/types/bookLocation/bookLocation';
import { customSelectStyles } from './customSelectStyles';

export type LocationOption = {
  label: string;
  value: string;
  type: 'image' | 'video' | 'action' | 'default';
};

type LocationGroupOptions = {
  label: string;
  options: LocationOption[];
};

function NoOptionsMessage() {
  return (
    <div style={{ color: '#999', padding: '10px', textAlign: 'center' }}>
      No locations available
    </div>
  );
}

interface LocationSelectProps {
  disabled?: boolean;
  value: string;
  onChange: (value: string) => void;
  locations: BookLocation[];
}

export function LocationSelect({
  disabled,
  value,
  onChange,
  locations,
}: LocationSelectProps) {
  const defaultLocation = locations.find((location) => location.isDefault);
  const isEmptyValue = !value;

  const groupedOptions: LocationGroupOptions[] = [
    {
      label: 'Default',
      options: [
        {
          label: `${defaultLocation ? defaultLocation.title : 'No Default'}`,
          value: '',
          type: 'default',
        },
      ],
    },
    {
      label: 'Images',
      options: locations
        .filter((location) => !location.videoId)
        .map((location) => ({
          label: location.title,
          value: location.id.toString(),
          type: 'image',
        })),
    },
    {
      label: 'Video',
      options: locations
        .filter((location) => !!location.videoId)
        .map((location) => ({
          label: location.title,
          value: location.id.toString(),
          type: 'video',
        })),
    },
    {
      label: 'Actions',
      options: [
        {
          label: 'Add Location',
          value: 'add',
          type: 'action',
        },
      ],
    },
  ];

  const selectedOption = groupedOptions
    .flatMap((group) => group.options)
    .find((option) => {
      if (isEmptyValue && option.value === '') {
        return true;
      }
      return String(option.value) === String(value);
    });

  const handleChange = (option: LocationOption | null) => {
    if (option) {
      onChange(option.value);
    }
  };

  const handleFilterOption = (option: FilterOptionOption<LocationOption>, inputValue: string) => {
    const isLabelMatch = option.label.toLowerCase().includes(inputValue.toLowerCase());
    const isGroupMatch = option.data?.type.toLowerCase().includes(inputValue.toLowerCase());
    return isLabelMatch || isGroupMatch;
  };

  const formatOptionLabel = (option: LocationOption) => {
    if (option.type === 'action') {
      return <span title={option.label}>{option.label}</span>;
    }
    return <span title={option.label}>{option.label}</span>;
  };

  return (
    <Select
      isDisabled={disabled}
      className="react-select-container"
      classNamePrefix="react-select"
      options={groupedOptions}
      value={selectedOption}
      onChange={handleChange}
      placeholder="[Select]"
      components={{ NoOptionsMessage }}
      styles={customSelectStyles}
      filterOption={handleFilterOption}
      formatOptionLabel={formatOptionLabel}
    />
  );
}
